/**
 * API Client
 *
 * Centralized HTTP client with interceptors for authentication,
 * error handling, and request/response logging.
 */
import axios, { AxiosError, type AxiosInstance, type AxiosRequestConfig } from 'axios';
// Import directly from store to avoid circular dependency with auth barrel export
import { getAuthToken } from '@v2/features/auth/stores/auth.store';
import {
  ApiError,
  UnauthenticatedError,
  UnauthorizedError,
  NotFoundError,
  ValidationError,
  ServerError,
  NetworkError,
} from './errors';
import type { RequestConfig } from './types/common';

/**
 * Token refresh callback — registered by auth module to avoid circular imports.
 * Returns the new token string on success, or throws on failure.
 */
type TokenRefresher = () => Promise<string>;
type SessionExpiredHandler = () => void;
let _tokenRefresher: TokenRefresher | null = null;
let _onSessionExpired: SessionExpiredHandler | null = null;
let _refreshPromise: Promise<string> | null = null;

export function registerTokenRefresher(fn: TokenRefresher): void {
  _tokenRefresher = fn;
}

export function registerSessionExpiredHandler(fn: SessionExpiredHandler): void {
  _onSessionExpired = fn;
}

/**
 * Attempt a single token refresh, deduplicating concurrent calls.
 */
async function tryRefreshToken(): Promise<string> {
  if (!_tokenRefresher) throw new Error('No token refresher registered');
  if (_refreshPromise) return _refreshPromise;

  _refreshPromise = _tokenRefresher().finally(() => {
    _refreshPromise = null;
  });
  return _refreshPromise;
}

/**
 * Environment-based API URL mapping
 */
const API_URL_MAP: Record<string, string> = {
  'dojo.objectively.ai': 'https://neural1-api.azurewebsites.net',
  'dojo.objectively.win': 'https://neural1-api.azurewebsites.net',
  'cerebral1.azurewebsites.net': 'https://neural1-api.azurewebsites.net',
  'cerebral1-east.azurewebsites.net': 'https://neural1-api.azurewebsites.net',
  'cerebral2.azurewebsites.net': 'https://neural2-api.azurewebsites.net',
  'cerebral2-east.azurewebsites.net': 'https://neural2-api.azurewebsites.net',
  'cerebral3.azurewebsites.net': 'https://neural3-api.azurewebsites.net',
  localhost: 'http://localhost:3000',
};

/**
 * Get base URL based on current hostname
 */
export function getBaseUrl(): string {
  const envUrl = import.meta.env.VITE_PICADOR_BASE_URL;
  if (envUrl) return envUrl.replace(/\/+$/, '');

  if (typeof window === 'undefined') {
    return API_URL_MAP['localhost'];
  }
  return API_URL_MAP[window.location.hostname] ?? API_URL_MAP['localhost'];
}

/**
 * Create the axios instance with default configuration
 */
function createApiClient(): AxiosInstance {
  const client = axios.create({
    baseURL: getBaseUrl(),
    headers: {
      'Content-Type': 'application/json',
    },
    timeout: 30000,
  });

  // Request interceptor: Add auth token
  client.interceptors.request.use(
    config => {
      const token = getAuthToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      if (import.meta.env.DEV) {
        console.debug(`>> ${config.method?.toUpperCase()} ${config.url}`, config.data);
      }

      return config;
    },
    error => {
      return Promise.reject(error);
    }
  );

  // Response interceptor: Refresh token on 401, then retry once
  client.interceptors.response.use(
    response => {
      if (import.meta.env.DEV) {
        console.debug(
          `<< ${response.config.method?.toUpperCase()} ${response.config.url}`,
          response.data
        );
      }
      return response;
    },
    async (error: AxiosError) => {
      const config = error.config as AxiosRequestConfig & { _retried?: boolean; headers: Record<string, string> };

      if (error.response?.status === 401 && config && !config._retried && _tokenRefresher) {
        config._retried = true;
        try {
          const newToken = await tryRefreshToken();
          config.headers.Authorization = `Bearer ${newToken}`;
          return client.request(config);
        } catch {
          // Refresh failed — force logout and redirect
          if (_onSessionExpired) {
            _onSessionExpired();
            // Return a never-resolving promise to suppress downstream error handling
            // while the redirect is in progress
            return new Promise(() => {});
          }
        }
      }

      throw transformError(error);
    }
  );

  return client;
}

/**
 * Transform Axios error into typed API error
 */
function transformError(error: AxiosError): ApiError {
  // Network error (no response)
  if (!error.response) {
    if (error.code === 'ECONNABORTED') {
      return new NetworkError('Request timeout');
    }
    return new NetworkError(error.message || 'Network error');
  }

  const status = error.response.status;
  const data = error.response.data as Record<string, unknown> | undefined;
  const message = (data?.message as string) || (data?.error as string) || error.message;

  switch (status) {
    case 401:
      return new UnauthenticatedError(message);
    case 403:
      return new UnauthorizedError(message);
    case 404:
      return new NotFoundError(message);
    case 422:
      return new ValidationError(message, data?.details as Record<string, string[]>);
    default:
      if (status >= 500) {
        return new ServerError(status, message);
      }
      return new ApiError(status, message, data);
  }
}

/**
 * The singleton API client instance
 */
const apiClient = createApiClient();

/**
 * Type-safe API methods
 */
export const api = {
  /**
   * GET request
   */
  async get<T>(url: string, config?: RequestConfig): Promise<T> {
    const response = await apiClient.get<T>(url, toAxiosConfig(config));
    return response.data;
  },

  /**
   * POST request
   */
  async post<T>(url: string, data?: unknown, config?: RequestConfig): Promise<T> {
    const response = await apiClient.post<T>(url, data, toAxiosConfig(config));
    return response.data;
  },

  /**
   * PUT request
   */
  async put<T>(url: string, data?: unknown, config?: RequestConfig): Promise<T> {
    const response = await apiClient.put<T>(url, data, toAxiosConfig(config));
    return response.data;
  },

  /**
   * PATCH request
   */
  async patch<T>(url: string, data?: unknown, config?: RequestConfig): Promise<T> {
    const response = await apiClient.patch<T>(url, data, toAxiosConfig(config));
    return response.data;
  },

  /**
   * DELETE request
   */
  async delete<T>(url: string, config?: RequestConfig): Promise<T> {
    const response = await apiClient.delete<T>(url, toAxiosConfig(config));
    return response.data;
  },
};

/**
 * Convert our RequestConfig to Axios config
 */
function toAxiosConfig(config?: RequestConfig): AxiosRequestConfig | undefined {
  if (!config) return undefined;

  return {
    signal: config.signal,
    headers: config.headers,
    params: config.params,
    data: config.data,
  };
}

/**
 * Export the raw axios instance for advanced use cases
 */
export { apiClient };

/**
 * API version prefixes
 */
export const API_V1 = '/api/v1';
export const API_V2 = '/api/v2';
